import { createReducer } from '@reduxjs/toolkit';
import {
  listUsers,
  filterUsers,
  searchUsers,
  exportUsers,
  blockUser,
  unblockUser,
  deleteUser,
  updateUser,
  getUserInfo,
  updateWalletBalance,
  updateUserCommission,
  listUserCommissions,
  getUserTransactionTrace,
} from '../actions/user';

const initialState = {
  users: {
    data: [],            // List of users (includes filtered or search results)
    currentPage: 1,      // Current page for pagination
    totalRecords: 0,       // Total number of records
    pageSize: 10,        // Items per page
    nextPageUrl: null,
    prevPageUrl: null
  },
  loading: false,        // Loading state for listing, filtering, or searching users
  error: null,           // General error state

  // Export state as an object
  export: {
    loading: false,      // Loading state for export action
    success: false,      // Tracks if export was successful
    error: null,         // Export-specific error
  },

  // States for blocking, unblocking, deleting, and updating users
  block: {
    success: false,
    error: null,
  },
  unblock: {
    success: false,
    error: null,
  },
  delete: {
    success: false,
    error: null,
  },
  update: {
    success: false,
    error: null,
  },

  // States for getting user info and updating wallet balance
  getUserInfo: {
    data: null,
    loading: false,
    error: null,
  },
  updateWalletBalance: {
    loading: false,
    error: null,
  },
  updateUserCommission: {
    success: false,
    error: null,
  },
  listUserCommissions: {
    data: [],            // List of user commissions
    loading: false,
    error: null,
  },
  
  // State for getting user transaction trace
  getUserTransactionTrace: {
    data: null,          // Transaction trace data
    loading: false,
    error: null,
  },
};

const usersReducer = createReducer(initialState, (builder) => {
  builder
  // Listing users (with pagination)
  .addCase(String(listUsers.trigger), (state) => {
    state.loading = true;
    state.error = null;
  })
  .addCase(String(listUsers.resolved), (state, action) => {
    const { 
      data: users, 
      current_page: currentPage, 
      next_page_url: nextPageUrl, 
      prev_page_url: prevPageUrl, 
      total: totalRecords, 
      per_page: pageSize 
    } = action.payload;

    state.loading = false;
    state.users = {
      data: users,
      currentPage, 
      totalRecords, 
      pageSize,
      nextPageUrl,
      prevPageUrl 
    };
  })
  .addCase(String(listUsers.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle listing error
  })

  // Filter users
  .addCase(String(filterUsers.trigger), (state) => {
    state.loading = true;
    state.error = null;
    state.users = {};
  })
  .addCase(String(filterUsers.resolved), (state, action) => {
    const { data: users, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.users = {
      data: users,    // Set fetched users data
      currentPage,    // Update current page from the backend
      totalRecords,     // Update total pages from the backend
      pageSize,       // Update page size (if provided by backend)
    };
  })
  .addCase(String(filterUsers.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle filtering error
  })

  // Search users
  .addCase(String(searchUsers.trigger), (state) => {
    state.loading = true;
    state.error = null;
    state.users = {};
  })
  .addCase(String(searchUsers.resolved), (state, action) => {
    const { data: users, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.users = {
      data: users,    // Set fetched users data
      currentPage,    // Update current page from the backend
      totalRecords,     // Update total pages from the backend
      pageSize,       // Update page size (if provided by backend)
    };
  })
  .addCase(String(searchUsers.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle search error
  })

  // Export users
  .addCase(String(exportUsers.trigger), (state) => {
    state.export.loading = true;  // Set export-specific loading
    state.export.success = false; // Reset success status
    state.export.error = null;    // Clear export errors
  })
  .addCase(String(exportUsers.resolved), (state) => {
    state.export.loading = false; // Stop export loading
    state.export.success = true;  // Set export success flag
  })
  .addCase(String(exportUsers.rejected), (state, action) => {
    state.export.loading = false; // Stop export loading
    state.export.success = false; // Mark export as unsuccessful
    state.export.error = action.payload; // Handle export error
  })

  // Block user
  .addCase(String(blockUser.trigger), (state) => {
    state.block.error = null;
  })
  .addCase(String(blockUser.resolved), (state) => {
    state.block.success = true;
  })
  .addCase(String(blockUser.rejected), (state, action) => {
    state.block.success = false;
    state.block.error = action.payload;
  })

  // Unblock user
  .addCase(String(unblockUser.trigger), (state) => {
    state.unblock.error = null;
  })
  .addCase(String(unblockUser.resolved), (state) => {
    state.unblock.success = true;
  })
  .addCase(String(unblockUser.rejected), (state, action) => {
    state.unblock.success = false;
    state.unblock.error = action.payload;
  })

  // Delete user
  .addCase(String(deleteUser.trigger), (state) => {
    state.delete.error = null;
  })
  .addCase(String(deleteUser.resolved), (state) => {
    state.delete.success = true;
  })
  .addCase(String(deleteUser.rejected), (state, action) => {
    state.delete.success = false;
    state.delete.error = action.payload;
  })

  // Update user details
  .addCase(String(updateUser.trigger), (state) => {
    state.update.error = null;
    state.update.loading = true;
  })
  .addCase(String(updateUser.resolved), (state) => {
    state.update.error = null;
    state.update.loading = false;
  })
  .addCase(String(updateUser.rejected), (state, action) => {
    state.update.loading = false;
    state.update.error = action.payload;
  })

  // Get user info
  .addCase(String(getUserInfo.trigger), (state) => {
    state.getUserInfo.loading = true;
    state.getUserInfo.error = null;
  })
  .addCase(String(getUserInfo.resolved), (state, action) => {
    state.getUserInfo.loading = false;
    state.getUserInfo.data = action.payload; // Set fetched user info
  })
  .addCase(String(getUserInfo.rejected), (state, action) => {
    state.getUserInfo.loading = false;
    state.getUserInfo.error = action.payload; // Handle error for getting user info
  })

  // Update wallet balance
  .addCase(String(updateWalletBalance.trigger), (state) => {
    state.updateWalletBalance.loading = true;
    state.updateWalletBalance.error = null;
  })
  .addCase(String(updateWalletBalance.resolved), (state) => {
    state.updateWalletBalance.loading = false;
    state.updateWalletBalance.error = null;
  })
  .addCase(String(updateWalletBalance.rejected), (state, action) => {
    state.updateWalletBalance.loading = false;
    state.updateWalletBalance.error = action.payload; // Handle error for updating wallet balance
  })

  // Update user commission
  .addCase(String(updateUserCommission.trigger), (state) => {
    state.updateUserCommission.loading = true;
    state.updateUserCommission.error = null;
  })
  .addCase(String(updateUserCommission.resolved), (state) => {
    state.updateUserCommission.loading = false;
    state.updateUserCommission.success = true;
  })
  .addCase(String(updateUserCommission.rejected), (state, action) => {
    state.updateUserCommission.loading = false;
    state.updateUserCommission.success = false;
    state.updateUserCommission.error = action.payload; // Handle error for updating user commission
  })

  // List user commissions
  .addCase(String(listUserCommissions.trigger), (state) => {
    state.listUserCommissions.loading = true;
    state.listUserCommissions.error = null;
  })
  .addCase(String(listUserCommissions.resolved), (state, action) => {
    const payload = action.payload?.data?.data ? action.payload.data : action.payload;
    
    const { 
      data: commissions, 
      current_page: currentPage, 
      next_page_url: nextPageUrl, 
      prev_page_url: prevPageUrl, 
      total: totalRecords, 
      per_page: pageSize 
    } = payload;

    state.listUserCommissions = {
      data: commissions,    // Set filtered services data
      currentPage,       // Update pagination after filtering
      totalRecords,
      pageSize,
      nextPageUrl,
      prevPageUrl,
      loading: false,
    };

  })
  .addCase(String(listUserCommissions.rejected), (state, action) => {
    state.listUserCommissions.loading = false;
    state.listUserCommissions.error = action.payload; // Handle error for listing user commissions
  })

  // Get user transaction trace
  .addCase(String(getUserTransactionTrace.trigger), (state) => {
    state.getUserTransactionTrace.loading = true;
    state.getUserTransactionTrace.error = null;
  })
  .addCase(String(getUserTransactionTrace.resolved), (state, action) => {
    state.getUserTransactionTrace.loading = false;
    state.getUserTransactionTrace.data = action.payload; // Set fetched transaction trace
  })
  .addCase(String(getUserTransactionTrace.rejected), (state, action) => {
    state.getUserTransactionTrace.loading = false;
    state.getUserTransactionTrace.error = action.payload; // Handle error for getting transaction trace
  })
});

export default usersReducer;
